import type { Document } from "@repo/types";
import { Formik } from "formik";
import * as yup from "yup";
import { Input } from "@repo/ui/components/Base/Input";
import { TextArea } from "@repo/ui/components/Base/TextArea";
import { Button } from "@repo/ui/components/Base/Button";
import { TagSelection } from "../tags";
import { DocumentSortableList } from "../dnd";
import { userStore, useUserStore } from "@/stores/userStore";
import { useContext, useRef } from "react";
import { Separator } from "@repo/ui/components/Base/Separator";
import { DynamicFormikFieldsWrapper } from "../common/DynamicFormikFieldsWrapper";
import { useTrackedNewObjects } from "@/hooks/trackCreatedObjects";
import { useBlockUnload } from "@repo/ui/components/Base/hooks/useBlockUnload";
import { SearchableDocumentsTable } from "../common/SearchableTables";
import { Label } from "@repo/ui/components/Base/Label";
import { AgendaItemDefaultsContext } from "./AgendaItemDefaults";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { TitleComposer } from "./TitleComposer";
import { CalendarCheck, ClockIcon } from "lucide-react";
import { LABEL_REVIEW_AGENDA_ITEM_BUTTON } from "@/lib/constants";
import { useGetAgendaItemMetadata } from "@/hooks/queryHooks/configHooks";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useActiveTeamIdOrOrg } from "@/hooks/useActiveTeam";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";

const getDefaultAgendaItemData = (): Partial<AgendaItemDto> => {
  const { activeTeam } = userStore.getState();
  const team = !activeTeam || activeTeam === "org" ? null : activeTeam.id;

  return {
    approver: team
      ? { approverType: "team", approverId: team }
      : { approverType: "none", approverId: "" },
  };
};

export interface AgendaItemFormProps {
  agendaItemFormData?: AgendaItemDto;
  partialAgendaItemFormData?: Partial<AgendaItemDto>;
  tenantId: string;
  handleCancel?: () => void;
  handleComplete?: () => void;
  onSubmit?: (agendaItem: AgendaItemDto) => void;
  addAnother?: () => void;
  viewOnly?: boolean;
  handleGoBack?: () => void;
  cancelButtonLabel?: string;
  withinDialog?: boolean;
  showReviewButton?: boolean;
  reviewButtonDisabled?: boolean;
  onReviewButtonClick?: () => void;
}

export const AgendaItemForm = ({
  agendaItemFormData,
  partialAgendaItemFormData,
  tenantId,
  handleCancel,
  handleComplete,
  onSubmit,
  addAnother,
  viewOnly,
  handleGoBack,
  cancelButtonLabel,
  withinDialog,
  showReviewButton,
  reviewButtonDisabled,
  onReviewButtonClick,
}: AgendaItemFormProps) => {
  useBlockUnload(!viewOnly);

  const user = useUserStore((s) => s.user);
  const activeTeamId = useActiveTeamIdOrOrg();
  const isFullOrg = activeTeamId === "org";

  const { data: documentsUnfiltered } = useDocumentList(
    tenantId,
    activeTeamId ?? ""
  );
  useMeetingList(tenantId, "org"); // make sure meetings are cached for agenda item 'pending'

  const documents = isFullOrg
    ? documentsUnfiltered?.filter((doc) => doc.createdBy === user?.id)
    : documentsUnfiltered;

  const { data: agendaItemMetadata } = useGetAgendaItemMetadata();

  const validationSchema = yup.object({
    name: yup.string().required("Item name is required"),
    ...DynamicFormikFieldsWrapper.validationFields(
      agendaItemMetadata?.fields ?? []
    ),
  });

  const trackNew = useTrackedNewObjects();
  const { defaults, showPending } = useContext(AgendaItemDefaultsContext) ?? {
    defaults: null,
    showPending: true,
  };

  const formAction = useRef<"add" | "addAnother">("add");
  const scrollBoxRef = useRef<HTMLDivElement>(null);

  if (!activeTeamId || !user) return <div>Loading...</div>;

  return (
    <div className="w-full">
      <Formik
        initialValues={
          agendaItemFormData
            ? { ...agendaItemFormData }
            : agendaItemRepo.init({
                ...getDefaultAgendaItemData(),
                ...defaults,
                teamId: activeTeamId,
                ...partialAgendaItemFormData,
              })
        }
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          if (!tenantId) {
            console.log("No tenant ID", tenantId);
            return;
          }
          const updatedAgendaItem = agendaItemFormData
            ? await agendaItemRepo.update(tenantId, values.id, { ...values })
            : await agendaItemRepo.create(tenantId, { ...values });

          if (!agendaItemFormData) trackNew("agendaItem", updatedAgendaItem);
          if (updatedAgendaItem) onSubmit?.(updatedAgendaItem);

          handleComplete && handleComplete();
          if (formAction.current === "addAnother" && addAnother) {
            setTimeout(addAnother, 300);
          }
        }}
      >
        {({
          values,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          errors,
          touched,
          isValid,
          isSubmitting,
          submitForm,
        }) => {
          const onAddDocument = (item: Document) => {
            setFieldValue(
              "documentIds",
              values.documentIds ? [...values.documentIds, item.id] : [item.id]
            );
            setFieldValue(
              "documents",
              values.documents ? [...values.documents, item] : [item]
            );
          };

          const pending = agendaItemRepo.isPending(values);

          const footer = (
            <>
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  viewOnly ? handleGoBack?.() : handleCancel?.();
                }}
              >
                {cancelButtonLabel ?? (viewOnly ? "Go Back" : "Cancel")}
              </Button>
              <div className="flex-1" />

              {!viewOnly && addAnother ? (
                <Button
                  type="button"
                  disabled={!isValid || isSubmitting}
                  onClick={() => {
                    formAction.current = "addAnother";
                    submitForm();
                  }}
                >
                  Save and Add Another
                </Button>
              ) : null}

              {showReviewButton && (
                <>
                  <div className="flex-1" />
                  <Button
                    disabled={reviewButtonDisabled}
                    variant="default"
                    onClick={() => {
                      onReviewButtonClick?.();
                    }}
                  >
                    {LABEL_REVIEW_AGENDA_ITEM_BUTTON}
                  </Button>
                </>
              )}

              {!viewOnly && (
                <Button type="submit" disabled={!isValid || isSubmitting}>
                  Save
                </Button>
              )}
            </>
          );

          const form = (
            <div className="flex flex-col gap-y-4">
              {/* title constructor */}
              {!viewOnly && (
                <TitleComposer
                  isNew={!agendaItemFormData}
                  isSubmitting={isSubmitting}
                  value={values.name}
                  onChange={function (next: string): void {
                    setFieldValue("name", next);
                  }}
                />
              )}
              {/* title preview */}
              <Input
                disabled
                id="name"
                name="name"
                value={values.name}
                label={viewOnly ? "Item Title" : ""}
              />
              <TextArea
                disabled={viewOnly || isSubmitting}
                label="Public Description"
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.description && touched.description}
                errorMessage={errors.description}
              />
              {showPending && (
                <div className="flex flex-row items-center justify-between">
                  {pending === null ? (
                    <p className="flex flex-row items-center gap-2">
                      Loading...
                    </p>
                  ) : pending ? (
                    <p className="flex flex-row items-center gap-2">
                      <ClockIcon /> Pending
                    </p>
                  ) : (
                    <p className="flex flex-row items-center gap-2">
                      <CalendarCheck /> Not Pending
                    </p>
                  )}

                  <p className="ml-4 text-balance">
                    Pending indicates the item has not been added to an agenda.
                  </p>
                </div>
              )}
              {agendaItemMetadata && (
                <>
                  <Separator />
                  <DynamicFormikFieldsWrapper
                    disabled={viewOnly || isSubmitting}
                    errors={errors}
                    fields={agendaItemMetadata.fields}
                    layout={agendaItemMetadata.layout}
                    values={values}
                  />
                </>
              )}
              <Separator />
              <TagSelection
                disabled={viewOnly || isSubmitting}
                label="Tags"
                value={values.tags ?? []}
                onChange={(newTags) => setFieldValue("tags", newTags)}
                enableAutocomplete
                useStandardizedTags
              />
              {documents && (
                <>
                  <Label label="Attached Documents" />
                  <DocumentSortableList
                    disabled={viewOnly || isSubmitting}
                    items={
                      values.documentIds
                        ? values.documentIds.map((doc) => {
                            return documents.find((d) => d.id === doc)!;
                          })
                        : []
                    }
                    onChange={(items) => {
                      setFieldValue(
                        "documentIds",
                        items.map((item) => item.id)
                      );
                      setFieldValue("documents", items);
                    }}
                    editableLabel="Edit Document"
                    deleteLabel="Remove Document"
                  />
                </>
              )}
              {documents && !viewOnly && (
                <SearchableDocumentsTable
                  disabled={isSubmitting}
                  data={documents.filter(
                    (doc) => !values.documentIds?.includes(doc.id)
                  )}
                  newPartialDocument={
                    isFullOrg
                      ? {
                          teamId:
                            values.approver?.approverType === "team"
                              ? values.approver?.approverId || "org"
                              : "org",
                        }
                      : undefined
                  }
                  onSubmit={onAddDocument}
                  onAdd={onAddDocument}
                />
              )}
              {!withinDialog && (
                <div className="flex flex-row justify-between gap-4">
                  {footer}
                </div>
              )}
            </div>
          );

          return (
            <form onSubmit={handleSubmit}>
              {withinDialog ? (
                <>
                  <DialogBody ref={scrollBoxRef}>{form}</DialogBody>
                  <DialogFooter className="justify-between">
                    {footer}
                  </DialogFooter>
                </>
              ) : (
                form
              )}
            </form>
          );
        }}
      </Formik>
    </div>
  );
};
